import { produce } from 'immer';
import { jwtDecode } from 'jwt-decode';
import { create } from 'zustand';

import type { IUserRoleEnum } from '~/enums/userRole.enum';
import { useTokenStore } from '~/stores/token/useTokenStore';
import { toClaimArray } from '~/utils/auth/normalizeClaim';
import type { IAuthTokenSlice } from './useAuthTokenSlice';
import useAuthTokenSlice from './useAuthTokenSlice';
import type { IChangePasswordSlice } from './useChangePasswordSlice';
import useChangePasswordSlice from './useChangePasswordSlice';
import type { IEmailConfirmationSlice } from './useEmailConfirmationSlice';
import useEmailConfirmationSlice from './useEmailConfirmationSlice';
import type { IExpiredUserPasswordSlice } from './useExpiredUserPasswordSlice';
import useExpiredUserPasswordSlice from './useExpiredUserPasswordSlice';
import type { ILoginSlice } from './useLoginSlice';
import useLoginSlice from './useLoginSlice';
import type { IRegisterSlice } from './useRegisterSlice';
import useRegisterSlice from './useRegisterSlice';
import type { IRememberPasswordSlice } from './useRememberPasswordSlice';
import useRememberPasswordSlice from './useRememberPasswordSlice';

export type ITokenClaim = {
  roleId: IUserRoleEnum[];
  identifier: number;
  email: string;
  name: string;
  allowedSystems: string[];
};

export type IVeilingTokemClaim = {
  veiling_claim_type_role: string | string[];
  veiling_claim_type_identifier: string;
  veiling_claim_type_email: string;
  veiling_claim_type_name: string;
  veiling_claim_type_allowed_systems: string | string[];
};

export type IAuthStore = {
  setData: (fn: (state: IAuthStore) => void) => void;

  tokenClaim: ITokenClaim;

  // slices antigas
  loginSlice: ILoginSlice;
  rememberPasswordSlice: IRememberPasswordSlice;
  emailConfirmationSlice: IEmailConfirmationSlice;
  registerSlice: IRegisterSlice;
  changePasswordSlice: IChangePasswordSlice;
  expiredUserPasswordSlice: IExpiredUserPasswordSlice;

  // slice novo (auth/token)
  authTokenSlice: IAuthTokenSlice;
};

export const defaultTokenClaim: ITokenClaim = {
  roleId: [],
  identifier: 0,
  email: '',
  name: '',
  allowedSystems: [],
};

function getInitialTokenClaim(): ITokenClaim {
  try {
    if (typeof window === 'undefined') return defaultTokenClaim;

    const token = useTokenStore.getAccess();
    if (!token) return defaultTokenClaim;

    const decoded = jwtDecode(token) as Partial<IVeilingTokemClaim> | null;
    if (!decoded) return defaultTokenClaim;

    return {
      roleId: toClaimArray(decoded.veiling_claim_type_role).map(Number) as IUserRoleEnum[],
      identifier: Number(decoded.veiling_claim_type_identifier ?? 0),
      email: decoded.veiling_claim_type_email ?? '',
      name: decoded.veiling_claim_type_name ?? '',
      allowedSystems: toClaimArray(decoded.veiling_claim_type_allowed_systems),
    } satisfies ITokenClaim;
  } catch {
    return defaultTokenClaim;
  }
}

const useAuthStore = create<IAuthStore>()((set, get) => ({
  // states
  tokenClaim: getInitialTokenClaim(),

  // slices antigas
  loginSlice: useLoginSlice(set, get),
  rememberPasswordSlice: useRememberPasswordSlice(set, get),
  emailConfirmationSlice: useEmailConfirmationSlice(set, get),
  registerSlice: useRegisterSlice(set, get),
  changePasswordSlice: useChangePasswordSlice(set, get),
  expiredUserPasswordSlice: useExpiredUserPasswordSlice(set, get),

  // slice novo
  authTokenSlice: useAuthTokenSlice(set, get),

  // setter padrão
  setData: fn => set(produce(fn)),
}));

export default useAuthStore;
